import { Button, Pagination, Table, TableProps, TextFilter } from '@amzn/awsui-components-react'
import { useCollection } from '@amzn/awsui-collection-hooks'
import EmptyState from './EmptyState'
import { getMatchesCountText } from './AllocationUtils'

interface FilterableDataTableProps<T> {
    items: readonly T[]
    columnDefinitions: TableProps.ColumnDefinition<T>[]
    // Shown when there are no items at all.
    emptyText: string
    // Noun for the built-in "No <noun> match your filter." empty-filter message (e.g. 'rows').
    itemNoun: string
    filteringPlaceholder: string
    filteringAriaLabel: string
    pageSize?: number
}

// The shared embedded-table pattern used across the import wizard: filtering, pagination, and a
// consistent no-match slot. Owns its own useCollection so callers only pass data, columns, and
// labels. Generic over the row type so column definitions stay type-safe.
const FilterableDataTable = <T,>({
    items,
    columnDefinitions,
    emptyText,
    itemNoun,
    filteringPlaceholder,
    filteringAriaLabel,
    pageSize = 10,
}: FilterableDataTableProps<T>) => {
    const {
        items: pageItems,
        actions,
        filteredItemsCount,
        collectionProps,
        filterProps,
        paginationProps,
    } = useCollection(items, {
        filtering: {
            empty: emptyText,
            noMatch: (
                <EmptyState
                    title='No matches'
                    subtitle={`No ${itemNoun} match your filter.`}
                    action={<Button onClick={() => actions.setFiltering('')}>Clear filter</Button>}
                />
            ),
        },
        pagination: { pageSize },
        sorting: {},
    })

    return (
        <Table
            {...collectionProps}
            variant='embedded'
            wrapLines
            items={pageItems}
            empty={emptyText}
            columnDefinitions={columnDefinitions}
            filter={
                <TextFilter
                    {...filterProps}
                    filteringPlaceholder={filteringPlaceholder}
                    filteringAriaLabel={filteringAriaLabel}
                    countText={getMatchesCountText(filteredItemsCount)}
                />
            }
            pagination={<Pagination {...paginationProps} />}
        />
    )
}

export default FilterableDataTable
